// =====================================================================
// MouvementFormModal.jsx - Create/edit PalletMovement (M-005 B2.2)
// =====================================================================
// Special: radio TRANSPORTER/CLIENT determina dropdown afisat:
//   - TRANSPORTER -> SearchableSelect cu carriers (GLS + alti)
//   - CLIENT      -> SearchableSelect cu projects (96 active)
// =====================================================================

import { useState, useEffect } from 'react';
import {
  palletMovements as movsApi,
  palletTypes as typesApi,
  projects as projectsApi,
  tripCarriers as carriersApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import SearchableSelect from '../../components/SearchableSelect.jsx';

const today = () => new Date().toISOString().slice(0, 10);

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: 8, padding: '1.5rem 1.75rem', width: '90%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' };
const inputStyle = { width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 0, padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnDanger = { background: 'transparent', color: '#b91c1c', border: '1px solid #fca5a5', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };

const radioCardActive = {
  flex: 1,
  border: '2px solid #2563eb',
  borderRadius: 6,
  padding: '0.75rem',
  cursor: 'pointer',
  background: '#eff6ff',
  textAlign: 'center',
};
const radioCardInactive = {
  flex: 1,
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '0.75rem',
  cursor: 'pointer',
  background: '#fff',
  textAlign: 'center',
};

export default function MouvementFormModal({ movementId, onClose, onSaved }) {
  const { user, can } = useAuth();
  const isEdit = !!movementId;

  const canCreate = can('pallet_tracking.create.all') || can('pallet_tracking.create.own');
  const canUpdate = can('pallet_tracking.update.all');
  const canUpdateOwn = can('pallet_tracking.update.own');
  const canDelete = can('pallet_tracking.delete.all');

  // Form state
  const [date, setDate]                   = useState(today());
  const [recipientType, setRecipType]     = useState('TRANSPORTER');
  const [selectedCarrier, setCarrier]     = useState(null);
  const [selectedProject, setProject]     = useState(null);
  const [palletTypeId, setPalletTypeId]   = useState('');
  const [palettesIn, setPalettesIn]       = useState('');
  const [palettesOut, setPalettesOut]     = useState('');
  const [description, setDescription]     = useState('');
  const [notes, setNotes]                 = useState('');

  // Meta
  const [ref, setRef]                     = useState('');
  const [invoiced, setInvoiced]           = useState(false);
  const [respName, setRespName]           = useState('');
  const [respUserId, setRespUserId]       = useState(null);

  // Dropdown data
  const [palletTypes, setPalletTypes]     = useState([]);
  const [allCarriers, setAllCarriers]     = useState([]);
  const [allProjects, setAllProjects]     = useState([]);

  // UI
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  // Création inline d'un transporteur (sans quitter le formulaire)
  const [showNewCarrier, setShowNewCarrier] = useState(false);
  const [ncCode, setNcCode]   = useState('');
  const [ncLabel, setNcLabel] = useState('');
  const [ncSaving, setNcSaving] = useState(false);
  const [ncError, setNcError] = useState(null);
  const canCreateCarrier = can('trips.manage_catalog.all') || can('pallet_tracking.create.all') || user?.role === 'SUPER_ADMIN';

  // Load: types + carriers + projects + movement (if edit)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [typesRes, carriersRes, projsRes] = await Promise.all([
          typesApi.list({ isActive: 'true', pageSize: 100 }),
          carriersApi.list({ isActive: 'true', pageSize: 200 }),
          projectsApi.list({ isActive: true }),
        ]);
        if (cancelled) return;
        setPalletTypes(typesRes.items || []);
        setAllCarriers(carriersRes.items || []);
        setAllProjects(Array.isArray(projsRes) ? projsRes.sort((a, b) => a.name.localeCompare(b.name)) : []);

        if (isEdit) {
          const m = await movsApi.get(movementId);
          if (cancelled) return;
          setRef(m.ref);
          setDate((m.date || '').slice(0, 10));
          setRecipType(m.recipientType);
          if (m.recipientType === 'TRANSPORTER' && m.carrier) setCarrier(m.carrier);
          if (m.recipientType === 'CLIENT' && m.project) setProject(m.project);
          setPalletTypeId(m.palletTypeId);
          setPalettesIn(m.palettesIn > 0 ? String(m.palettesIn) : '');
          setPalettesOut(m.palettesOut > 0 ? String(m.palettesOut) : '');
          setDescription(m.description || '');
          setNotes(m.notes || '');
          setInvoiced(m.invoiced);
          setRespName(m.responsible?.name || '');
          setRespUserId(m.responsibleUserId || m.responsible?.id || null);
        } else {
          // Pre-fill default pallet type (EURO if exista)
          const euro = (typesRes.items || []).find(t => t.code === 'EURO');
          if (euro) setPalletTypeId(euro.id);
          else if (typesRes.items?.[0]) setPalletTypeId(typesRes.items[0].id);
        }
      } catch (err) {
        console.error('[MouvementFormModal] load:', err);
        setError(err.response?.data?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [movementId, isEdit]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleRecipChange(newType) {
    if (newType === recipientType) return;
    setRecipType(newType);
    // Reset opposite selection
    if (newType === 'TRANSPORTER') setProject(null);
    else setCarrier(null);
  }

  async function handleCreateCarrier() {
    setNcError(null);
    const code = ncCode.trim().toUpperCase();
    const label = ncLabel.trim();
    if (!code || !label) return setNcError('Code et nom requis');
    if (!/^[A-Z0-9_-]+$/.test(code)) return setNcError('Code: MAJUSCULES, chiffres, _ et - uniquement');
    setNcSaving(true);
    try {
      const created = await carriersApi.create({ code, label });
      // normalise (SearchableSelect affiche .name ; TripCarrier expose .label)
      const norm = { ...created, name: created.name || created.label || label };
      setAllCarriers(prev => [...prev, norm].sort((a, b) => (a.name || a.label || '').localeCompare(b.name || b.label || '')));
      setCarrier(norm);            // sélection auto du nouveau transporteur
      setShowNewCarrier(false);
      setNcCode(''); setNcLabel('');
    } catch (err) {
      setNcError(err.response?.data?.message || 'Erreur lors de la création du transporteur');
    } finally {
      setNcSaving(false);
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (!date) return setError('Date requise');
    if (recipientType === 'TRANSPORTER' && !selectedCarrier) return setError('Transporteur requis');
    if (recipientType === 'CLIENT' && !selectedProject)     return setError('Client (projet) requis');
    if (!palletTypeId) return setError('Type de palette requis');

    const pIn  = parseInt(palettesIn  || '0', 10);
    const pOut = parseInt(palettesOut || '0', 10);
    if (pIn < 0 || pOut < 0) return setError('Quantités doivent être >= 0');
    if (pIn === 0 && pOut === 0) return setError('Au moins une entrée ou une sortie est requise');

    setSaving(true);
    try {
      const data = {
        date,
        recipientType,
        carrierId:    recipientType === 'TRANSPORTER' ? selectedCarrier.id : null,
        projectId:    recipientType === 'CLIENT'      ? selectedProject.id : null,
        palletTypeId,
        palettesIn:   pIn,
        palettesOut:  pOut,
        description:  description.trim() || null,
        notes:        notes.trim() || null,
      };
      if (isEdit) await movsApi.update(movementId, data);
      else        await movsApi.create(data);
      onSaved();
    } catch (err) {
      console.error('[MouvementFormModal] save:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer le mouvement ${ref} ?`)) return;
    setSaving(true);
    try {
      await movsApi.remove(movementId);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  }

  // Day 11++ (01-06-2026) — own-scoped edit: update.all OU (propriétaire + update.own), tant que NON facturé.
  const isOwnMovement = !!respUserId && respUserId === user?.id;
  const canEditThis = canUpdate || (isOwnMovement && canUpdateOwn);
  const formDisabled = invoiced || saving || (isEdit && !canEditThis);

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEdit ? 'Modifier mouvement' : 'Nouveau mouvement'}
          </h2>
          {isEdit && <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>{ref}</span>}
        </div>

        {invoiced && (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>
            ⚠ Ce mouvement est déjà facturé. Modifications et suppression bloquées.
          </div>
        )}

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Date *</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} disabled={formDisabled} required />
          </div>

          {/* Recipient type radio (cards) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Destinataire *</label>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div
                style={recipientType === 'TRANSPORTER' ? radioCardActive : radioCardInactive}
                onClick={() => !formDisabled && handleRecipChange('TRANSPORTER')}
              >
                <div style={{ fontSize: '1.5rem' }}>🚛</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, marginTop: 4, color: recipientType === 'TRANSPORTER' ? '#1e40af' : '#374151' }}>
                  Transporteur
                </div>
                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                  GLS, etc.
                </div>
              </div>
              <div
                style={recipientType === 'CLIENT' ? radioCardActive : radioCardInactive}
                onClick={() => !formDisabled && handleRecipChange('CLIENT')}
              >
                <div style={{ fontSize: '1.5rem' }}>🏢</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, marginTop: 4, color: recipientType === 'CLIENT' ? '#1e40af' : '#374151' }}>
                  Client
                </div>
                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                  Projet
                </div>
              </div>
            </div>
          </div>

          {/* Conditional select - carrier OR project */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>
              {recipientType === 'TRANSPORTER' ? 'Transporteur *' : 'Client (projet) *'}
            </label>
            {recipientType === 'TRANSPORTER' ? (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      items={allCarriers}
                      selected={selectedCarrier}
                      onSelect={setCarrier}
                      disabled={formDisabled}
                      placeholder="🔍 Rechercher transporteur..."
                      getPrimary={(c) => c.name || c.label}
                      getSecondary={(c) => c.notes || ''}
                      searchFields={[(c) => c.name || c.label, (c) => c.notes]}
                    />
                  </div>
                  {canCreateCarrier && !formDisabled && (
                    <button type="button" style={{ ...btnSecondary, whiteSpace: 'nowrap', padding: '0.5rem 0.875rem' }}
                      onClick={() => { setShowNewCarrier(v => !v); setNcError(null); }}>
                      {showNewCarrier ? '× Fermer' : '＋ Nouveau'}
                    </button>
                  )}
                </div>
                {showNewCarrier && (
                  <div style={{ marginTop: '0.625rem', padding: '0.75rem', border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 6 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' }}>＋ Nouveau transporteur</div>
                    {ncError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.375rem 0.625rem', borderRadius: 4, marginBottom: '0.5rem', fontSize: '0.75rem' }}>{ncError}</div>}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input value={ncCode} onChange={e => setNcCode(e.target.value.toUpperCase())} placeholder="Code (ex: DACHSER)" style={{ ...inputStyle, flex: 1 }} maxLength={50} />
                      <input value={ncLabel} onChange={e => setNcLabel(e.target.value)} placeholder="Nom (ex: Dachser Belgium)" style={{ ...inputStyle, flex: 2 }} maxLength={100}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCarrier(); } }} />
                      <button type="button" style={{ ...btnPrimary, whiteSpace: 'nowrap' }} onClick={handleCreateCarrier} disabled={ncSaving}>
                        {ncSaving ? '...' : 'Créer'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <SearchableSelect
                items={allProjects}
                selected={selectedProject}
                onSelect={setProject}
                disabled={formDisabled}
                placeholder="🔍 Rechercher client (projet)..."
                getPrimary={(p) => p.name}
                getSecondary={(p) => `${p.kind} · ${p.id}`}
                searchFields={[(p) => p.name, (p) => p.id]}
              />
            )}
          </div>

          {/* Pallet type */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Type de palette *</label>
            <select value={palletTypeId} onChange={e => setPalletTypeId(e.target.value)} style={inputStyle} disabled={formDisabled} required>
              <option value="">— Sélectionner —</option>
              {palletTypes.map(t => (
                <option key={t.id} value={t.id}>{t.code} — {t.label}</option>
              ))}
            </select>
          </div>

          {/* Quantities - in/out */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ ...labelStyle, color: '#065f46' }}>Entrées (reçus)</label>
              <input
                type="number"
                value={palettesIn}
                onChange={e => setPalettesIn(e.target.value)}
                style={inputStyle}
                min="0"
                max="99999"
                disabled={formDisabled}
                placeholder="0"
              />
            </div>
            <div>
              <label style={{ ...labelStyle, color: '#991b1b' }}>Sorties (expédiés)</label>
              <input
                type="number"
                value={palettesOut}
                onChange={e => setPalettesOut(e.target.value)}
                style={inputStyle}
                min="0"
                max="99999"
                disabled={formDisabled}
                placeholder="0"
              />
            </div>
          </div>
          <small style={{ color: '#9ca3af', fontSize: '0.6875rem', display: 'block', marginTop: '-0.5rem', marginBottom: '1rem' }}>
            Au moins une des deux quantités doit être &gt; 0.
          </small>

          {/* Description (short) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Description courte (optionnel)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={inputStyle}
              maxLength={200}
              disabled={formDisabled}
              placeholder="Ex: Échange palettes hebdo"
            />
          </div>

          {/* Notes (longer) */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              maxLength={500}
              disabled={formDisabled}
            />
          </div>

          {/* Edit metadata */}
          {isEdit && respName && (
            <div style={{ background: '#f9fafb', padding: '0.75rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
              <div>Responsable: <strong>{respName}</strong></div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {isEdit && canDelete && !invoiced && (
                <button type="button" style={btnDanger} onClick={handleDelete} disabled={saving}>Supprimer</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>Annuler</button>
              {(canCreate || (isEdit && canEditThis && !invoiced)) && (
                <button type="submit" style={btnPrimary} disabled={saving || invoiced}>
                  {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
